import _mapInstanceProperty from "@babel/runtime-corejs3/core-js/instance/map";
import _filterInstanceProperty from "@babel/runtime-corejs3/core-js/instance/filter";
import { defineComponent, computed, ref, watchEffect, watchPostEffect, onMounted } from "@vue/composition-api";
import ReportTabLayout from "../report-tab-layout.vue";
import ReportToolbar from "../report-toolbar.vue";
import MaterialInput from "@Shared/components/inputs/material-input.vue";
import { capitalize, cloneDeep, isEmpty } from "lodash";
import { useI18n } from "vue-i18n-composable";
import { APPLICATION_TYPE_OPTIONS } from "@Shared/@Utils/constants";
import SelectLevel from "../select-level.vue";
import SelectApplicationStatus from "../select-application-status.vue";
import SelectFormat from "../select-format.vue";
import SelectLender from "../select-lender.vue";
import DateSection from "@Shared/components/sections/date-section.vue";
import Field from "@Shared/components/sections/field.vue";
import AgentsSelect from "@Broker/@Modules/agents/components/agents-select.vue";
import { useMultipleSelect } from "@Shared/@Composables/generic/useMultipleSelect";
import ReferrersSelect from "@Broker/@Modules/clients/referrers/components/referrers-select.vue";
import ApplicationSummaryLoader from "./application-summary-loader.vue";
import { useReportsStoreGetters } from "../../store/reports-helpers";
import * as ReportsGetters from "../../store/reports-getters";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import { useVuelidate } from "@Shared/@Composables/validations/useVuelidate";
import { useAccountStoreGetters } from "@Broker/@Modules/main/store/account/account-helpers";
import * as AccountGetters from "@Broker/@Modules/main/store/account/account-getters";
import { REPORT_APPLICATION_STATUS_E, REPORT_APPLICATION_TYPE_E, REPORT_DATE_TYPE_E, REPORT_GROUP_BY_E, REPORT_LEVEL_E, REPORT_SORT_BY_E } from "@Broker/@Types/reports/types";
import { useReportDefaultSettings } from "@Broker/@Composables/reports/useReportDefaultSettings";
import vSelectField from "@Shared/components/form-elements/fields/v-select-field.vue";
export default defineComponent({
  name: "ApplicationSummaryContainer",
  components: {
    ReportTabLayout,
    ReportToolbar,
    MaterialInput,
    SelectLevel,
    SelectApplicationStatus,
    SelectFormat,
    SelectLender,
    DateSection,
    Field,
    AgentsSelect,
    ReferrersSelect,
    ApplicationSummaryLoader,
    vSelectField
  },
  mixins: [validationMixin],
  validations() {
    return {
      settings: {
        level: {
          required
        },
        format: {
          required
        },
        lender: {
          required
        },
        applicationStatus: {
          required: requiredIf(() => this.atLeastOneSelectedTypeAndLender)
        },
        applicationType: {
          required: requiredIf(() => this.atLeastOneSelectedStatusAndLender)
        },
        dateType: {
          required
        },
        groupBy: {
          required
        },
        dateFrom: {
          required
        },
        dateTo: {
          required
        },
        agents: {
          required: requiredIf(() => this.showAgentSelect)
        },
        referralSource: {
          required
        }
      }
    };
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    runningReport: {
      type: Object,
      default: () => ({
        loading: false,
        loadingSucceeded: true
      })
    }
  },
  setup(props, {
    emit
  }) {
    const {
      t,
      tc
    } = useI18n();
    const {
      handleSelectMultipleChange,
      handleVSelectMultipleChange
    } = useMultipleSelect();
    const {
      v$
    } = useVuelidate();
    const agentsSelect = ref();
    const applicationTypeSelect = ref();
    const referrersSelect = ref();
    const unsyncedAppsModal = ref();
    const defaultReferrers = ref();
    const action = ref();
    const selectAllOption = [{
      id: "all",
      username: "all",
      givenName: capitalize(tc("global.words.all", 1)),
      familyName: "",
      fullName: capitalize(tc("global.words.all", 1))
    }];
    const {
      GET_APPLICATION_SUMMARY_STRUCTURE
    } = useReportsStoreGetters([ReportsGetters.GET_APPLICATION_SUMMARY_STRUCTURE]);
    const {
      CURRENT_ACCOUNT_IS_BROKER,
      CURRENT_USER_USERNAME
    } = useAccountStoreGetters([AccountGetters.CURRENT_ACCOUNT_IS_BROKER, AccountGetters.CURRENT_USER_USERNAME]);
    const localLoading = ref(false);
    const applicationSummaryStructure = cloneDeep(GET_APPLICATION_SUMMARY_STRUCTURE.value);
    const settings = ref(applicationSummaryStructure);
    const resetToDefaultProps = ref({
      disabled: false,
      loading: false,
      loadingSucceeded: true
    });
    const setDefaultProps = ref({
      disabled: false,
      loading: false,
      loadingSucceeded: true
    });
    const options = computed(() => ({
      placeholder: t("global.actions.chooseOption")
    }));
    const brokerOption = computed(() => settings.value.level === REPORT_LEVEL_E.BROKERAGE ? [{
      id: REPORT_LEVEL_E.BROKERAGE,
      text: t("global.doorr.brokerage")
    }] : []);
    let selectsData = computed(() => ({
      applicationType: [{
        id: "all",
        text: capitalize(tc("global.words.all", 1))
      }, ...APPLICATION_TYPE_OPTIONS()],
      dateType: [{
        id: REPORT_DATE_TYPE_E.CLOSING_DATE,
        text: t("global.doorr.closingDate")
      }, {
        id: REPORT_DATE_TYPE_E.DATE_CREATED,
        text: t("global.doorr.dateCreated")
      }, {
        text: t("application-reports.firm-statistics.approvalDate"),
        id: REPORT_DATE_TYPE_E.APPROVAL_DATE
      }],
      groupBy: [{
        id: REPORT_GROUP_BY_E.APPLICATION_STATUS,
        text: t("application-reports.applicationStatus")
      }, {
        id: REPORT_GROUP_BY_E.LENDER,
        text: t("global.doorr.lender")
      }, {
        id: REPORT_GROUP_BY_E.MORTGAGE_TYPE,
        text: t("application-reports.mortgageType")
      }, {
        id: REPORT_GROUP_BY_E.APPLICATION_TYPE,
        text: t("application-reports.applicationType")
      }, {
        id: REPORT_GROUP_BY_E.AGENT,
        text: capitalize(tc("global.doorr.agent", 1))
      }, ...brokerOption.value],
      sortBy: [{
        id: REPORT_SORT_BY_E.LENDER,
        text: t("global.doorr.lender")
      }, {
        id: REPORT_SORT_BY_E.MORTGAGE_TYPE,
        text: t("application-reports.mortgageType")
      }, {
        id: REPORT_SORT_BY_E.APPLICATION_TYPE,
        text: t("application-reports.applicationType")
      }, {
        id: REPORT_SORT_BY_E.APPLICANT_SURNAME,
        text: t("application-reports.applications-summary.applicantSurname")
      }, {
        id: REPORT_SORT_BY_E.APPLICATION_ID,
        text: t("application.applicationID")
      }, {
        id: REPORT_SORT_BY_E.CLOSING_DATE,
        text: t("global.doorr.closingDate")
      }, {
        id: REPORT_SORT_BY_E.AGENT,
        text: capitalize(tc("global.doorr.agent", 1))
      }, ...brokerOption.value]
    }));
    const showAgentSelect = computed(() => CURRENT_ACCOUNT_IS_BROKER.value && settings.value.level === REPORT_LEVEL_E.AGENT);
    const atLeastOneSelectedStatusAndLender = computed(() => settings.value.applicationStatus == "" && settings.value.lender == "");
    const atLeastOneSelectedTypeAndLender = computed(() => settings.value.applicationType == "" && settings.value.lender == "");
    onMounted(() => {
      setDefaultAgentIfNeeded();
      if (settings.value.referralSource != "all") {
        defaultReferrers.value = settings.value.referralSource;
      }
    });
    const formatData = data => {
      var _data$map, _data;
      data = cloneDeep(data);
      return (_data$map = (_data = data) == null ? void 0 : _mapInstanceProperty(_data).call(_data, ref => {
        var _ref$name;
        ref.id = ref.value;
        ref.text = (_ref$name = ref.name) != null ? _ref$name : ref.label;
        return ref;
      })) != null ? _data$map : [];
    };
    const handleChangeDates = range => {
      settings.value.dateFrom = range.start;
      settings.value.dateTo = range.end;
    };
    const handleSelectAgentChange = (value, type) => {
      settings.value.agents = handleVSelectMultipleChange(value, type, settings.value.agents, true);
    };
    const handleChangeApplicationType = (selectedValue, type) => {
      var _applicationTypeSelec;
      if (selectedValue.id === "all" && type === "select") (_applicationTypeSelec = applicationTypeSelect.value) == null ? void 0 : _applicationTypeSelec.close();
      action.value = "";
      settings.value.applicationType = handleSelectMultipleChange(selectedValue, type, settings.value.applicationType, true);
    };
    const handleChangeReferralSource = (selectedValue, type) => {
      settings.value.referralSource = handleVSelectMultipleChange(type == "select" ? selectedValue.pop() : selectedValue, type, settings.value.referralSource, true);
    };
    const primaryOptions = computed(() => {
      var _context;
      return _filterInstanceProperty(_context = selectsData.value.sortBy).call(_context, val => val.id !== settings.value.sortBySecondary);
    });
    const secondaryOptions = computed(() => {
      var _context2;
      return _filterInstanceProperty(_context2 = selectsData.value.sortBy).call(_context2, val => val.id !== settings.value.sortByPrimary);
    });
    const handleClearSelection = () => {
      settings.value = cloneDeep(applicationSummaryStructure);
      setDefaultAgentIfNeeded();
      defaultReferrers.value = [];
      action.value = "clear";
    };
    const {
      loadReportDefaultValues,
      updateReportDefaults
    } = useReportDefaultSettings({
      reportType: "applicationSummary",
      reportDataRef: settings
    });
    const handleResetToDefault = () => {
      loadReportDefaultValues();
      setDefaultAgentIfNeeded();
      defaultReferrers.value = settings.value.referralSource;
      action.value = "reset";
    };
    const handleSetDefault = async () => {
      setDefaultProps.value.loading = true;
      await updateReportDefaults(settings.value);
      setDefaultProps.value.loading = false;
    };
    const handleRunReport = async () => {
      emit("run-report", {
        reportType: "application-summary",
        data: cloneDeep(settings.value)
      });
    };
    const setDefaultAgentIfNeeded = () => {
      if (!CURRENT_ACCOUNT_IS_BROKER.value) settings.value.agents = [CURRENT_USER_USERNAME.value];
    };
    watchPostEffect(() => {
      if (isEmpty(settings.value.applicationType)) {
        settings.value.applicationType = REPORT_APPLICATION_TYPE_E.ALL;
      }
      if (isEmpty(settings.value.applicationStatus)) {
        settings.value.applicationStatus = REPORT_APPLICATION_STATUS_E.ALL;
      }
      if (isEmpty(settings.value.lender)) {
        settings.value.lender = "all";
      }
    });
    watchEffect(() => {
      if (settings.value.level === REPORT_LEVEL_E.BROKERAGE) {
        settings.value.agents = "all";
      }
    });
    return {
      localLoading,
      settings,
      resetToDefaultProps,
      setDefaultProps,
      options,
      v$,
      agentsSelect,
      applicationTypeSelect,
      referrersSelect,
      unsyncedAppsModal,
      defaultReferrers,
      action,
      selectsData,
      showAgentSelect,
      selectAllOption,
      atLeastOneSelectedTypeAndLender,
      atLeastOneSelectedStatusAndLender,
      primaryOptions,
      secondaryOptions,
      formatData,
      handleSelectMultipleChange,
      handleVSelectMultipleChange,
      handleChangeDates,
      handleSelectAgentChange,
      handleChangeApplicationType,
      handleChangeReferralSource,
      handleClearSelection,
      handleResetToDefault,
      handleSetDefault,
      handleRunReport,
      setDefaultAgentIfNeeded
    };
  }
});